import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useNewMerchantInformationAPI from "../../api/merchantInformationAPI";
import { roleAuth } from "../../../utils/functionAuthorization";
import { setFlag, setResponseInfo } from "../../../features/responseSlice";
import LegalDetailsPanel from "../../Components/MerchantInformation/LegalDetailsPanel";
import MerchantDetailsPanel from "../../Components/MerchantInformation/MerchantDetailsPanel";
import "../../Styles/pages/merchant-information.scss";

const ACCESS_POINTS = {
  legalInfo: "VIEW_LEGAL_ENTITY_GENERAL_INFORMATION",
  editProfile: "EDIT_MERCHANT_PROFILE_INFORMATION",
  editPicture: "EDIT_LOGO_AND_PROFILE_PICTURES",
  setLimits: "SET_MERCHANT_TRANSACTIONS_LIMITS",
  conversion: "SWITCH_AUTO_CONVERSION",
  merchantInfo: "VIEW_MERCHANT_INFORMATION",
  addMerchant: "ADD_NEW_MERCHANT",
  editPaymentInfo: "EDIT_MERCHANT_PAYMENT_INFO",
};

export default function MerchantInformationPage({
  onOpenQrAfterGenerate,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { flag } = useSelector((state) => state.transferResponse);
  const merchantDetailsFromRedux = useSelector(
    (state) => state.app?.details?.merchantInformation || {},
  );
  const merchantsFromRedux = useSelector(
    (state) => state.app?.tables?.merchantInformation?.data || [],
  );
  const merchantLoadingRedux = useSelector((state) =>
    Boolean(state.app?.tables?.merchantInformation?.loading),
  );
  const {
    GetMerchantInformation,
    EditPaymentConfig,
    merchantInformation,
    merchantLoading,
  } = useNewMerchantInformationAPI();
  const [activeMerchantId, setActiveMerchantId] = useState("");
  const getMerchantInformationRef = useRef(GetMerchantInformation);

  getMerchantInformationRef.current = GetMerchantInformation;

  const entityType = sessionStorage.getItem("EntityType") || "";
  const isLegalEntity = entityType === "LEGALENTITY";
  const canViewLegalInfo = roleAuth("merchantinformation", ACCESS_POINTS.legalInfo);
  const canViewMerchantInfo = roleAuth("merchantinformation", ACCESS_POINTS.merchantInfo);
  const canAddMerchant = roleAuth("merchantinformation", ACCESS_POINTS.addMerchant);

  useEffect(() => {
    void getMerchantInformationRef.current();
  }, []);

  useEffect(() => {
    if (![0, 1].includes(flag)) return;

    void getMerchantInformationRef.current();
    dispatch(setFlag(null));
    dispatch(setResponseInfo({}));
  }, [dispatch, flag]);

  const merchants = useMemo(
    () => {
      if (Array.isArray(merchantsFromRedux) && merchantsFromRedux.length > 0) {
        return merchantsFromRedux;
      }
      return merchantInformation?.MerchantsInfoList || [];
    },
    [merchantInformation, merchantsFromRedux],
  );

  const legalInformation =
    merchantDetailsFromRedux?.LegalInformation || merchantInformation?.LegalInformation;
  const isMerchantLoading = merchantLoadingRedux || merchantLoading;

  useEffect(() => {
    if (!merchants.length) {
      setActiveMerchantId("");
      return;
    }

    const hasActiveMerchant = merchants.some(
      (merchant) => String(merchant?.MerchantID) === String(activeMerchantId),
    );

    if (!hasActiveMerchant) {
      setActiveMerchantId(String(merchants[0]?.MerchantID || ""));
    }
  }, [activeMerchantId, merchants]);

  const selectedMerchant = useMemo(() => {
    if (!merchants.length) return null;

    return (
      merchants.find(
        (merchant) => String(merchant?.MerchantID) === String(activeMerchantId),
      ) || merchants[0]
    );
  }, [activeMerchantId, merchants]);

  const merchantTabs = useMemo(
    () =>
      merchants.map((merchant) => ({
        id: String(merchant?.MerchantID),
        label:
          merchant?.MerchantGeneralInfo?.MerchantLegalName ||
          merchant?.MerchantName ||
          `Merchant ${merchant?.MerchantID}`,
        aliases: [
          merchant?.MerchantGeneralInfo?.MerchantLegalName,
          merchant?.MerchantName,
        ].filter(Boolean),
      })),
    [merchants],
  );

  const pageTitle = isLegalEntity ? "Legal Information" : "Merchant Information";
  const canShowStandaloneAddMerchant =
    isLegalEntity && canAddMerchant && !canViewMerchantInfo;

  return (
    <section className="new-merchant-information">
      <div className="new-merchant-information__hero">
        <div>
          <span className="new-merchant-information__eyebrow">Settings</span>
          <h1>{pageTitle}</h1>
          <p>
            Review entity details, update merchant-facing information, and manage merchant
            payment settings.
          </p>
        </div>

        {canShowStandaloneAddMerchant ? (
          <button
            type="button"
            className="new-merchant-information__primary-button"
            onClick={() => navigate("/merchant-enrollment/1")}
          >
            Add Merchant
          </button>
        ) : null}
      </div>

      {isLegalEntity && canViewLegalInfo ? (
        <LegalDetailsPanel
          loading={isMerchantLoading}
          entity={legalInformation}
        />
      ) : null}
    


      {/* {isMerchantLoading ? (
        <MerchantDetailsPanel
          merchant={selectedMerchant || {}}
          merchantLoading
          accessPoints={isLegalEntity ? ACCESS_POINTS : undefined}
          onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          onEditPaymentConfig={EditPaymentConfig}
          merchantTabs={isLegalEntity && canViewMerchantInfo ? merchantTabs : []}
          activeMerchantId={String(selectedMerchant?.MerchantID || "")}
          onMerchantChange={setActiveMerchantId}
          canAddMerchant={isLegalEntity && canAddMerchant}
          onAddMerchant={() => navigate("/merchant-enrollment/1")}
        />
      ) : selectedMerchant ? (
        <MerchantDetailsPanel
          merchant={selectedMerchant}
          merchantLoading={isMerchantLoading}
          accessPoints={isLegalEntity ? ACCESS_POINTS : undefined}
          onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          onEditPaymentConfig={EditPaymentConfig}
          merchantTabs={isLegalEntity && canViewMerchantInfo ? merchantTabs : []}
          activeMerchantId={String(selectedMerchant?.MerchantID || "")}
          onMerchantChange={setActiveMerchantId}
          canAddMerchant={isLegalEntity && canAddMerchant}
          onAddMerchant={() => navigate("/merchant-enrollment/1")}
        />
      ) : (
        <section className="new-merchant-information__section-card">
          <div className="new-merchant-information__empty-state">
            <h3>No merchant information available</h3>
            <p>Once merchant data is available, it will appear here.</p>
            {isLegalEntity && canAddMerchant ? (
              <button
                type="button"
                className="new-merchant-information__primary-button"
                onClick={() => navigate("/merchant-enrollment/1")}
              >
                Add Merchant
              </button>
            ) : null}
          </div>
        </section>
      )} */}
    </section>
  );
}
